import { SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Grid from "@mui/material/Unstable_Grid2";
import { Alert, CircularProgress } from "@mui/material";
import { useNavigate } from "react-router-dom";

import { PageWrapper, PageWrapperProps } from "../../common-ui/PageWrapper";
import { useAppSelector } from "../../app/hooks";
import {
  ProgramFormErrors,
  ProgramNetworkErrors,
  useCreateProgram
} from "./programUtils";
import { CreateProgramInput } from "../../API";
import { useEffect } from "react";
import {
  selectCurrentUser,
  selectUserCognitoGroups
} from "../Users/usersSlice";
import { CognitoUserGroups } from "../Users/userUtils";
import {
  alphabetizeInstitutions,
  useAllInstitutions
} from "../Institutions/institutionUtils";
import {
  selectNetworks,
  selectNetworksStatus
} from "../Networks/networksSlice";
import { EditProgramInputs } from "../../app/types";
import { useUpdateProgramNetworks } from "../Networks/networkUtils";

const pageDetails: PageWrapperProps = {
  className: "NewProgram",
  headerText: "New Program"
};

export const NewProgram = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentUser = useAppSelector(selectCurrentUser);
  const currentUsersPermissions = useAppSelector(selectUserCognitoGroups);

  // Networks
  const allNetworks = useAppSelector(selectNetworks);
  const allNetworksStatus = useAppSelector(selectNetworksStatus);

  const isSysAdmin = currentUsersPermissions?.includes(
    CognitoUserGroups.SysAdmins
  );
  const currentInstitution = currentUser?.institution;
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<EditProgramInputs>({
    defaultValues: {
      institutionId: currentInstitution?.id,
      associatedNetworkIds: []
    }
  });
  const {
    status: createProgramStatus,
    error: createProgramError,
    createProgram
  } = useCreateProgram();
  const {
    institutions,
    status: useAllInstitutionsStatus,
    error: useAllInstitutionsError
  } = useAllInstitutions();
  const alphabetizedInstitutions = institutions
    ? alphabetizeInstitutions(institutions)
    : institutions;
  const { updateProgramNetworks, status: updateProgramStatus } =
    useUpdateProgramNetworks();

  const onSubmit: SubmitHandler<EditProgramInputs> = async (data) => {
    const { name, description, institutionId, associatedNetworkIds } = data;
    if (!name || !institutionId) return;
    // Parse CreateProgramInput and send to createProgram
    const createProgramInput: CreateProgramInput = {
      name: name,
      description: description,
      institutionId: institutionId,
      associatedNetworkIds
    };
    const result = await createProgram(createProgramInput);

    // Capture returned item (to get new Program id)
    if (result !== null) {
      navigate(`/programs/${result.id}`);
    } else {
      console.error("Failed to create program: API returned null");
    }
  };

  const anyApiIsLoading = [
    createProgramStatus,
    useAllInstitutionsStatus,
    allNetworksStatus,
    updateProgramStatus
  ].includes("loading");
  const missingNeededInstitution = !isSysAdmin && !currentUser?.institutionId;

  const statusOfRequiredInitialData = [
    useAllInstitutionsStatus,
    allNetworksStatus
  ];

  const requiredInitialDataIdle = statusOfRequiredInitialData.every(
    (status) => status === "idle"
  );
  const loadingRequiredInitialData =
    statusOfRequiredInitialData.includes("loading");

  useEffect(() => {
    // If a user is a SysAdmin, they will select the institution from the dropdown.
    // Otherwise, they will have the institution inferred from their account.
    if (!isSysAdmin) {
      reset({
        institutionId: currentInstitution?.id
      });
    }
  }, [currentUser, isSysAdmin]);

  // No requests have yet been made to feth the data.
  if (requiredInitialDataIdle) {
    return (
      <PageWrapper {...pageDetails}>
        <h2>{t("Awaiting data...")}</h2>
      </PageWrapper>
    );
  }

  // Loading of necessary data is in progress.
  if (loadingRequiredInitialData) {
    return (
      <PageWrapper {...pageDetails}>
        <CircularProgress />
      </PageWrapper>
    );
  }

  if (createProgramStatus === "failed" || createProgramError) {
    return (
      <PageWrapper {...pageDetails}>
        <h2>{t("Error")}</h2>
        <h3>{t(ProgramNetworkErrors.CreateProgramError)}</h3>
        {createProgramError && <p>{createProgramError}</p>}
      </PageWrapper>
    );
  }

  return (
    <PageWrapper {...pageDetails}>
      <form
        className="NewProgramForm"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
      >
        <Grid spacing={2} direction="column">
          {missingNeededInstitution && (
            <Grid xs={8}>
              <Alert severity="warning">
                {t(ProgramFormErrors.NoInstitution)}
              </Alert>
            </Grid>
          )}
          {isSysAdmin && (
            <div>
              <Grid container spacing={2}>
                <Grid xs={8}>
                  <label htmlFor="institution-select">{t("Institution")}</label>
                  <select
                    required
                    disabled={anyApiIsLoading}
                    id="institution-select"
                    style={{ width: "100%" }}
                    {...register("institutionId")}
                  >
                    {alphabetizedInstitutions?.map((institution) => (
                      <option value={institution.id}>{institution.name}</option>
                    ))}
                  </select>
                </Grid>
                <Grid xs={8}>
                  <label htmlFor="networks-select">{t("Networks")}</label>
                  <br />
                  {allNetworks?.map((network, index) => (
                    <p>
                      {" "}
                      <label>
                        <input
                          key={index + network.id}
                          type="checkbox"
                          value={network.id}
                          disabled={anyApiIsLoading}
                          {...register(`associatedNetworkIds`)}
                        />{" "}
                        {network.name}
                      </label>
                    </p>
                  ))}
                </Grid>
              </Grid>
            </div>
          )}
          <Grid xs={8}>
            <label htmlFor="name">{t("Program Name")}</label>
            <input
              type="text"
              required
              placeholder={t("Program Name")}
              style={{ width: "100%" }}
              id="name"
              disabled={missingNeededInstitution || anyApiIsLoading}
              {...register("name", { required: true })}
            />
            {errors.name && (
              <Alert severity="error">
                {t(ProgramFormErrors.EmptyProgramName)}
              </Alert>
            )}
          </Grid>
          <Grid xs={8}>
            <label htmlFor="description">{t("Program Description")}</label>
            <textarea
              placeholder={t("Program Description")}
              style={{ width: "100%" }}
              id="description"
              disabled={missingNeededInstitution || anyApiIsLoading}
              {...register("description")}
            />
          </Grid>
        </Grid>
        <button disabled={missingNeededInstitution || anyApiIsLoading}>
          {t("Save")}
        </button>
        <button
          disabled={anyApiIsLoading}
          onClick={() => navigate("/programs")}
        >
          {t("Cancel")}
        </button>
      </form>
    </PageWrapper>
  );
};
